import { memo, RefObject, useEffect } from 'react';

import cn from 'classnames';

import { OResponsiveModal } from '@src/shared';

import PanelistInviteContent from './PanelistInviteContent';
import PanelistInviteQRContent from './PanelistInviteQRContent';

import usePanelistInviteModal from './usePanelistInviteModal';

import { useRoomContext } from '@src/contexts';
import { useRoomActionsStore } from '@src/stores';

import styles from './panelistInviteModal.module.scss';

interface IPanelistInviteModalProps {
  shareButtonRef: RefObject<HTMLDivElement> | null;
}

const PanelistInviteModal = ({ shareButtonRef }: IPanelistInviteModalProps) => {
  const { isMobileDevice } = useRoomContext();

  const {
    isInviteOpen,
    isQRView,
    handleInviteModalToggle,
    handleBackToInvite,
    handleOpenQr,
    handleDownloadQR,
    meetingLink,
  } = usePanelistInviteModal();

  useEffect(() => {
    if (!shareButtonRef?.current) return;

    useRoomActionsStore.setState((state) => ({
      shareRoomSettings: {
        ...state.shareRoomSettings,
        onClickCallback: () => handleInviteModalToggle(),
      },
    }));
  }, [handleInviteModalToggle, shareButtonRef]);

  if (!shareButtonRef?.current) return null;

  const modalTitle = isQRView ? 'QR Code' : 'Invite to Webinar';
  const headerClass = isQRView ? `${styles.modalHeader} ${styles.modalHeaderWithBack}` : styles.modalHeader;

  return (
    <OResponsiveModal
      title={modalTitle}
      isOpen={isInviteOpen}
      onClose={handleInviteModalToggle}
      hasBackdrop
      oModalProps={{
        titleWrapperAlignment: isQRView ? 'left' : 'center',
        anchorEl: shareButtonRef.current,
        showBackButton: isQRView,
        onBackClick: handleBackToInvite,
        classes: {
          header: headerClass,
          container: styles.modalContainer,
        },
      }}
      modalClasses={{
        header: headerClass,
      }}
      {...(isQRView
        ? {
            drawerActions: {
              onCancel: handleBackToInvite,
              cancelLabel: 'Back',
            },
          }
        : {})}
    >
      <div className={cn({ [styles.responsiveModalContainer]: isMobileDevice })}>
        {isQRView ? (
          <PanelistInviteQRContent meetingLink={meetingLink} handleDownloadQR={handleDownloadQR} />
        ) : (
          <PanelistInviteContent handleOpenQr={handleOpenQr} />
        )}
      </div>
    </OResponsiveModal>
  );
};

export default memo(PanelistInviteModal);
